import React, { useEffect, useState } from 'react';
import {
  getDecryptedAccessToken,
  getDecryptedData,
  getDecryptedUserType
} from '../../Utils/LoginUserDetails.js/LoginUserSavedDetails';
import ApiServices from '../../Utils/ApiServices/ApiServices';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Button,
  Chip,
  Stack,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';

const PatientMembership = ({ patientData }) => {
  const navigate = useNavigate();

  const LoginUserDetails = getDecryptedData();
  const LoginUserType = getDecryptedUserType();
  const accessToken = getDecryptedAccessToken();

  const pid = LoginUserType === 'Patient' ? LoginUserDetails?.pid : patientData?.pid;
  const uuid = patientData?.uuid;

  const [membershipData, setMembershipData] = useState([]);
  const [apiError, setApiError] = useState(false);

  const [openJoinDialog, setOpenJoinDialog] = useState(false);
  const [referralKey, setReferralKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchMembership = () => {
    if (!pid) return;

    const apiUrl =
      LoginUserType === 'Patient'
        ? `/apis/default/portal/membership/patient/${pid}`
        : `/apis/default/api/membership/patient/${pid}`;

    ApiServices.get(apiUrl, {
      Authorization: `Bearer ${accessToken}`
    })
      .then((res) => {
        if (Array.isArray(res?.data) && res.data.length > 0) {
          const sortedData = [...res.data].sort((a, b) =>
            new Date(b.start_date) - new Date(a.start_date)
          );
          setMembershipData(sortedData);
          setApiError(false);
        } else {
          setApiError(true);
        }
      })
      .catch((err) => {
        console.error('Membership API Error:', err);
        setApiError(true);
      });
  };

  useEffect(() => {
    fetchMembership();
  }, [pid, LoginUserType, accessToken]);

  const handleRedirect = () => {
    if (LoginUserType === 'Patient') {
      navigate('/patient/available-membership');
    } else {
      navigate(`/provider/patient/available-membership/${uuid}/${pid}`);
    }
  };

  const handleSubmitReferral = async () => {
    if (!referralKey) return;

    setSubmitting(true);
    try {
      const res = await ApiServices.post(
        `/apis/default/api/membership/patient/${pid}`,
        { referral_key: referralKey },
        { Authorization: `Bearer ${accessToken}` }
      );
   setSnackbar({ 
  open: true, 
  message: res.data.status === "failure" ? "Invalid or expired referral key." : "Joined successfully!", 
  severity: res.data.status === "failure" ? "error" : "success" 
});

      fetchMembership();
      setOpenJoinDialog(false);
      setReferralKey('');
    } catch (err) {
      console.error('Referral Error:', err);
     setSnackbar({
  open: true,
  message: err?.response?.data?.message || 'Failed to join using referral key',
  severity: 'error'
});

    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ color: '#028391', mb: 2 }}>
        Membership
      </Typography>

   {/* When no membership found */}
{apiError || membershipData.length === 0 ? (
  <Card
    sx={{
      height: '100%',
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      border: '2px solid #028391',
      borderRadius: '12px',
      minHeight: '230px',
      gap: 2
    }}
  >
    <Typography variant="body1" color="text.secondary">
      No membership found. Please purchase a membership from available plans.
    </Typography>
    <Button
      variant="contained"
      sx={{ backgroundColor: '#028391' }}
      onClick={handleRedirect}
    >
      View Plans
    </Button>

    {/* Join Button inside card */}
    <Button
      variant="outlined"
      color="primary"
      onClick={() => setOpenJoinDialog(true)}
    >
      Join Your Family/Business Membership Plan
    </Button>
  </Card>
) : (
  membershipData.map((plan, index) => (
    <Card
      key={index}
      sx={{
        mb: 3,
        p: 3,
        border: '1.5px solid #028391',
        borderRadius: '16px',
        boxShadow: 3,
        backgroundColor: '#f9fafa'
      }}
    >
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#026e73' }}>
        {plan.plan_type}
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
        <Chip label={`Tier: ${plan.plan_tier}`} color="primary" />
        <Chip label={`Category: ${plan.category}`} color="secondary" />
        <Chip label={`Group: ${plan.group_name}`} sx={{ backgroundColor: '#81d4fa' }} />
        <Chip label={`Role: ${plan.role}`} color="info" />
        {plan.is_expired && (
          <Chip label="Expired" color="error" variant="outlined" />
        )}
      </Stack>

      <Divider sx={{ my: 2 }} />

      {plan.active === 0 && (
        <Box mt={2} textAlign="center">
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            Your membership is inactive. Please make a payment to activate it.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              if (LoginUserType === 'Patient') {
                navigate(`/patient/membership-details/${plan.group_id}/${plan.category}/${plan.plan_id}/${plan.role}`);
              } else {
                navigate(`/provider/patient/membership-details/${uuid}/${pid}/${plan.group_id}/${plan.category}/${plan.plan_id}`);
              }
            }}
          >
            Show Details
          </Button>
        </Box>
      )}

      {/* Join Button inside each membership card */}
      <Box mt={2}>
        <Button variant="outlined" color="primary" onClick={() => setOpenJoinDialog(true)}>
          Join Your Family/Business Membership Plan
        </Button>
      </Box>
    </Card>
  ))
)}


      {/* Join Referral Dialog */}
      <Dialog open={openJoinDialog} onClose={() => setOpenJoinDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Join Membership Using Referral Key</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Referral Key"
            value={referralKey}
            onChange={(e) => setReferralKey(e.target.value)}
            placeholder="Enter referral key"
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenJoinDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitReferral} variant="contained" color="primary" disabled={submitting}>
            {submitting ? <CircularProgress size={22} /> : 'Join'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientMembership;
